import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { CommonModule } from '@angular/common';
import { Component, ElementRef, Inject, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteActivatedEvent as MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { RxState } from '@rx-angular/state';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { BehaviorSubject, Observable, combineLatest, skipWhile } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';
import { FieldValidation } from '../field-validation';
import { InlineEditService } from '../inline-edit.service';
import { FieldWarningService } from './model-driven-form-error/field-warning.service';
import { CrmModelDrivenFormErrorComponent } from './model-driven-form-error/model-driven-form-error.component';
import {
  FieldOptionsDependencies,
  FieldOptionsInjectionToken,
  FieldValueInterface,
  ObjectType,
  RenderableFieldInterface,
} from '../../../tokens-and-interfaces';
import { SEARCH_DEBOUNCE_MS } from '../../../constants';

export class TagsFieldHandler implements RenderableFieldInterface {
  formControl: FormControl<string[]>;

  constructor(
    public objectType: ObjectType,
    public fieldId: string,
    public name: string,
    public label: string,
    public value: string[],
    public description: string,
    public readOnly = false,
    public maxTags: number | null = null,
    public maxTagLength: number | null = null,
    public addTagPrompt = '',
    public maximumTagsError = '',
    public validation: FieldValidation = new FieldValidation(),
  ) {
    this.formControl = new FormControl<string[]>([], { nonNullable: true });
    this.formControl.setValue(value);
  }

  setValidation(validation: FieldValidation): void {
    this.validation = validation;
    this.formControl.setValidators(validation.getValidators());
    this.formControl.setAsyncValidators(validation.getAsyncValidators());
  }

  hasModification(): boolean {
    return this.formControl?.dirty;
  }

  fieldValue(): FieldValueInterface {
    return {
      fieldId: this.fieldId,
      stringValues: { values: this.formControl.value },
    };
  }

  render(viewContainerRef: ViewContainerRef): void {
    viewContainerRef.clear();
    const component = viewContainerRef.createComponent<TagsRendererComponent>(TagsRendererComponent);
    component.instance.objectType = this.objectType;
    component.instance.fieldId = this.fieldId;
    component.instance.control = this.formControl;
    component.instance.label = this.label;
    component.instance.validation = this.validation;
    component.instance.readOnly = this.readOnly;
    component.instance.getTagOptions(this.fieldId);
    component.instance.maxTags = this.maxTags;
    component.instance.maxTagLength = this.maxTagLength;
    component.instance.addTagPrompt = this.addTagPrompt;
    component.instance.maximumTagsError = this.maximumTagsError;
  }
}

@Component({
  templateUrl: './tags.html',
  imports: [
    CommonModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatIconModule,
    MatInputModule,
    ReactiveFormsModule,
    TranslateModule,
    GalaxyBadgeModule,
    GalaxyFormFieldModule,
    GalaxyTooltipModule,
    CrmModelDrivenFormErrorComponent,
  ],
  providers: [RxState, FieldWarningService],
  styleUrls: ['./renderers.scss'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
})
export class TagsRendererComponent {
  fieldId!: string;
  objectType!: ObjectType;
  control!: FormControl<string[]>;
  label!: string;
  validation!: FieldValidation;
  readOnly!: boolean;

  newTagControl: FormControl<string> = new FormControl<string>('', { nonNullable: true });
  separatorKeysCodes: number[] = [ENTER, COMMA];
  maxTagLength: number | null = 0;
  availableTags$!: Observable<string[]>;
  maxTags: number | null = 0;
  isInputActive = false;
  initialized$ = new BehaviorSubject<boolean>(false);
  addTagPrompt = '';
  maximumTagsError = '';

  @ViewChild('tagInput') tagInput!: ElementRef<HTMLInputElement>;

  constructor(
    @Inject(FieldOptionsInjectionToken) private readonly fieldOptionsDependencies: FieldOptionsDependencies,
    private readonly state: RxState<ComponentState>,
    private readonly inlineEditService: InlineEditService,
  ) {
    const zeros: ComponentState = {
      existingTags: [],
      addedTags: [],
      availableTags: [],
    };
    this.state.set(zeros);

    this.availableTags$ = this.state.select('availableTags');
  }

  getTagOptions(fieldId: string): void {
    const initialized$ = this.initialized$.pipe(distinctUntilChanged());
    const tagControlChanges$ = this.newTagControl.valueChanges.pipe(startWith(this.newTagControl.value));

    const existingTags$ = combineLatest([tagControlChanges$, initialized$]).pipe(
      skipWhile(([, initialized]: [string, boolean]) => !initialized),
      map(([search]: [string, boolean]) => (search || '').trim()),
      debounceTime(SEARCH_DEBOUNCE_MS),
      distinctUntilChanged(),
      switchMap((search) => this.fieldOptionsDependencies.getFieldOptions(fieldId, search)),
      map((tags) => tags || []),
      map((tags) => [...tags].sort()),
    );
    this.state.connect('existingTags', existingTags$);

    const addedTags$ = this.control.valueChanges.pipe(
      startWith(this.control.value),
      map((tags) => tags || []),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
    );
    this.state.connect('addedTags', addedTags$);

    const availableTags$ = this.state.select().pipe(
      map((state) => [state.addedTags, state.existingTags]),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      map(([usedTags, existing]) => existing.filter((t) => !usedTags.includes(t))),
      map((tags) => [...tags].sort()),
    );

    this.state.connect('availableTags', availableTags$);
  }

  private addTag(tag: string) {
    const tags = this.control.value || [];
    if (tags.includes(tag)) {
      return;
    }
    this.control.setValue([...tags, tag]);
  }

  private touchControls(): void {
    this.control.markAsDirty();
    this.control.markAsTouched();
    this.control.updateValueAndValidity();
    this.newTagControl.setValue('');
    this.newTagControl.updateValueAndValidity();
  }

  addNewTag(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value) {
      this.addTag(value);
    }
    event?.chipInput?.clear();
    this.touchControls();
  }

  removeTag(tagIndex: number): void {
    if (tagIndex >= 0) {
      const tags = [...this.control.value];
      tags.splice(tagIndex, 1);
      this.control.setValue(tags);
    }
    this.touchControls();
  }

  selectExistingTag(event: MatAutocompleteSelectedEvent): void {
    this.tagInput.nativeElement.value = '';
    this.newTagControl.setValue('');

    const tag = event?.option?.viewValue;
    if (tag) {
      this.addTag(tag);
    }
    this.touchControls();
  }

  initializeField(): void {
    this.initialized$.next(true);
  }

  saveField(): void {
    this.inlineEditService.saveInlineEdit(this.objectType, {
      fieldId: this.fieldId,
      stringValues: { values: this.control.value },
    });
  }
}

interface ComponentState {
  existingTags: string[];
  addedTags: string[];
  availableTags: string[];
}
